import {useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import './CaseStudyBlock.css';
import heroImage from "../../assets/img/case-sports/hero-sports-data-clean.png";
import {ReactComponent as FieldOverlay} from "../../assets/img/case-sports/hero-sports-data-clean.svg";
import {ReactComponent as TrajectoriesOverlay} from "../../assets/img/case-sports/hero-sports-data-trajectories.svg";

const CaseStudyBlock = ({isDarkTheme}) => {
    const {t} = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const fieldOverlayRefs = useRef([]);
    const trajectoriesOverlayRef = useRef(null);

    useEffect(() => {
        const overlays = fieldOverlayRefs.current.filter(Boolean);

        if (!overlays.length) {
            return undefined;
        }

        const nodesByCoordinate = new Map();
        const nodes = [];

        /**
         * Reuses one animated node for each starting coordinate.
         */
        const getNode = (x, y, overlayIndex) => {
            const key = `${overlayIndex}:${x}:${y}`;

            if (nodesByCoordinate.has(key)) {
                return nodesByCoordinate.get(key);
            }

            const index = nodes.length;
            const node = {
                x,
                y,
                radiusX: 2 + (index % 3),
                radiusY: 1.5 + ((index + 1) % 3),
                speed: .00018 + index * .000025,
                phase: index * 1.7,
            };

            nodesByCoordinate.set(key, node);
            nodes.push(node);

            return node;
        };
        const connections = overlays.flatMap((overlay, overlayIndex) => Array.from(overlay.querySelectorAll('path[marker-start]')).map((path) => {
            const tokens = path.getAttribute('d').match(/[a-zA-Z]|-?\d*\.?\d+/g);
            let x = 0;
            let y = 0;
            let command;
            const connectionNodes = [];

            for (let index = 0; index < tokens.length;) {
                if (/[a-zA-Z]/.test(tokens[index])) {
                    command = tokens[index];
                    index += 1;
                }

                const pointX = Number(tokens[index]);
                const pointY = Number(tokens[index + 1]);
                const isRelative = command === 'm' || command === 'l';

                x = isRelative ? x + pointX : pointX;
                y = isRelative ? y + pointY : pointY;
                connectionNodes.push(getNode(x, y, overlayIndex));
                index += 2;

                if (command === 'M') {
                    command = 'L';
                }

                if (command === 'm') {
                    command = 'l';
                }
            }

            return {path, nodes: connectionNodes};
        }));
        const circles = overlays.flatMap((overlay, overlayIndex) => Array.from(overlay.querySelectorAll('g[fill="url(#nodes)"] circle')).map((circle) => ({
            circle,
            node: getNode(Number(circle.getAttribute('cx')), Number(circle.getAttribute('cy')), overlayIndex),
        })));
        let animationFrame;

        const animate = (time) => {
            nodes.forEach((node) => {
                const {x, y, radiusX, radiusY, speed, phase} = node;
                const angle = time * speed + phase;
                node.currentX = x + Math.sin(angle) * radiusX;
                node.currentY = y + Math.cos(angle * 1.23) * radiusY;
            });

            connections.forEach(({path, nodes: connectionNodes}) => {
                const [firstNode, ...otherNodes] = connectionNodes;
                const line = otherNodes.reduce(
                    (pathData, node) => `${pathData} ${node.currentX} ${node.currentY}`,
                    `M${firstNode.currentX} ${firstNode.currentY}`,
                );

                path.setAttribute('d', line);
            });

            circles.forEach(({circle, node}) => {
                circle.setAttribute('cx', node.currentX);
                circle.setAttribute('cy', node.currentY);
            });

            animationFrame = requestAnimationFrame(animate);
        };

        animationFrame = requestAnimationFrame(animate);

        return () => cancelAnimationFrame(animationFrame);
    }, []);

    useEffect(() => {
        const overlay = trajectoriesOverlayRef.current;

        if (!overlay) {
            return undefined;
        }

        const nodesByCoordinate = new Map();
        const nodes = [];
        const getNode = (x, y) => {
            const key = `${x}:${y}`;

            if (nodesByCoordinate.has(key)) {
                return nodesByCoordinate.get(key);
            }

            const index = nodes.length;
            const node = {
                x,
                y,
                circles: [],
                radiusX: 10 + (index % 15),
                radiusY: 10 + ((index + 7) % 15),
                speedX: .00045 + ((index * 73) % 97) * .000026,
                speedY: .0004 + ((index * 41) % 89) * .00003,
                phaseX: index * 2.399,
                phaseY: index * 1.713,
            };

            nodesByCoordinate.set(key, node);
            nodes.push(node);

            return node;
        };
        Array.from(overlay.querySelectorAll('circle')).forEach((circle) => {
            const node = getNode(Number(circle.getAttribute('cx')), Number(circle.getAttribute('cy')));

            node.circles.push(circle);
        });
        const paths = Array.from(overlay.querySelectorAll('path')).map((path) => {
            const data = path.getAttribute('d');
            const values = data.match(/-?\d*\.?\d+/g).map(Number);
            const isVertical = data.includes('V');
            const startNode = getNode(values[0], values[1]);
            const endNode = isVertical
                ? getNode(values[0], values[values.length - 1])
                : getNode(values[values.length - 2], values[values.length - 1]);

            return {path, data, values, isVertical, startNode, endNode};
        });
        let animationFrame;

        const animate = (time) => {
            nodes.forEach((node) => {
                const {x, y, radiusX, radiusY, speedX, speedY, phaseX, phaseY} = node;
                const xAngle = time * speedX + phaseX;
                const yAngle = time * speedY + phaseY;

                node.currentX = x + Math.sin(xAngle) * radiusX + Math.sin(yAngle * .37) * radiusX * .35;
                node.currentY = y + Math.cos(yAngle) * radiusY + Math.cos(xAngle * .53) * radiusY * .3;
                node.circles.forEach((circle) => {
                    circle.setAttribute('cx', node.currentX);
                    circle.setAttribute('cy', node.currentY);
                });
            });
            paths.forEach(({path, data, values, isVertical, startNode, endNode}) => {
                if (isVertical) {
                    path.setAttribute('d', `M${startNode.currentX} ${startNode.currentY}L${endNode.currentX} ${endNode.currentY}`);

                    return;
                }

                let index = 0;
                path.setAttribute('d', data.replace(/-?\d*\.?\d+/g, () => {
                    const value = index === 0 ? startNode.currentX
                        : index === 1 ? startNode.currentY
                            : index === values.length - 2 ? endNode.currentX
                                : index === values.length - 1 ? endNode.currentY
                                    : values[index];

                    index += 1;

                    return value;
                }));
            });
            animationFrame = requestAnimationFrame(animate);
        };

        animationFrame = requestAnimationFrame(animate);

        return () => cancelAnimationFrame(animationFrame);
    }, []);

    const dotRows = Array.from({length: 40}, (_, index) => {
        const offset = (index * .375) % 15;

        return {
            id: index + 1,
            animationDelay: `${-((15 - offset) % 15) - 2.3}s`,
        };
    });
    return (
        <article className={`${isOpen && 'case-study-open'} ${!isDarkTheme && 'case-study-dark'} case-study`}>
            <div className="case-study-header">
                <div className="project-header-block">
                    <h3 className={`${!isDarkTheme && 'project-header-dark'} project-header`}>
                        {t('Advanced Sports Statistics Search Made 10× Faster')}
                    </h3>
                    <div className="case-study-stack">
                        <span>python</span><span>PostgreSQL</span><span>CTE</span><span>partitioned tables</span><span>Redis</span><span>Golang</span>
                    </div>
                </div>
                <span className="case-study-action">
                    <button
                        className="case-study-trigger"
                        type="button"
                        aria-expanded={isOpen}
                        onClick={() => setIsOpen(!isOpen)}
                    >
                        <span className="case-study-toggle">{isOpen ? t('Show less') : t('Read more')}</span>
                    </button>
                    {!isOpen && (
                        <span className={`${!isDarkTheme && 'case-study-summary-dark'} case-study-summary`}>
                            {t('We optimized a player search platform with 183 filters, 173 metrics and 14.6M tagged events. The SQL plan was reduced from 12.85s to 1.27s while preserving percentile calculations, pagination and the existing business logic.')}
                        </span>
                    )}
                </span>
            </div>

            <div className={`${isOpen && 'case-study-content-open'} case-study-content`}>
                <div className="case-study-content-inner">
                    <div className="case-study-overview">
                        <div className="case-study-hero-wrap">
                            <div className="case-study-hero">
                                <img className="case-study-hero-image" src={heroImage} alt="Football pitch viewed from behind a goal"/>
                                <FieldOverlay ref={(element) => {
                                    fieldOverlayRefs.current[0] = element;
                                }} className="case-study-field-overlay" aria-hidden="true" focusable="false"/>
                                <FieldOverlay ref={(element) => {
                                    fieldOverlayRefs.current[1] = element;
                                }} className="case-study-field-overlay case-study-field-overlay-right" aria-hidden="true" focusable="false"/>
                                <TrajectoriesOverlay ref={trajectoriesOverlayRef} className="case-study-trajectories-overlay" aria-hidden="true" focusable="false"/>
                            </div>
                        </div>
                        <div className="case-study-details">
                            <section className={`${!isDarkTheme && 'case-study-text-dark'} case-study-intro`}>
                                {t('We developed player search for a sports platform across 183 filters, 173 metrics and 14.6M tagged events.')}
                            </section>
                            <section>
                                <h4>{t('The challenge')}</h4>
                                <p>{t('The search combined filtering, aggregations, rankings, percentiles and pagination. We needed to preserve complex business logic while making heavy queries faster.')}</p>
                            </section>
                            <section>
                                <h4>{t('The approach')}</h4>
                                <p>{t('We evolved the system gradually in stages: optimzed backend code along with PostgreSQL, added caching and page prewarming, then introduced data storage restucturing and a new Golang service. Each stage was introduced in parallel and verified independently to protect the existing business logic.')}</p>
                            </section>
                        </div>
                    </div>

                    <div className="case-study-analytics">
                        <section className="case-study-evolution">
                            <span className="case-study-dot-grid" aria-hidden="true">
                                {dotRows.map(({id, animationDelay}) => <span key={id} className="case-study-dot-row" style={{animationDelay}}/>)}
                            </span>
                            <span className="case-study-dot-filter" aria-hidden="true">
                                {dotRows.map(({id, animationDelay}) => <span key={id} className="case-study-dot-row" style={{animationDelay}}/>)}
                            </span>
                            <h4>{t('Managed evolution')}</h4>
                            <div className="case-study-evolution-flow">
                                <div className="case-study-stage case-study-stage-legacy">
                                    <strong>{t('Legacy')}</strong>
                                    <span>Django / ORM</span>
                                    <span>{t('Cache and prewarm')}</span>
                                </div>
                                <span className="case-study-arrow"/>
                                <div className="case-study-stage case-study-stage-v2">
                                    <strong>V2</strong>
                                    <span>{t('Denormalized tables')}</span>
                                    <span>{t('Separate path')}</span>
                                </div>
                                <span className="case-study-arrow"/>
                                <div className="case-study-stage case-study-stage-v3">
                                    <strong>V3</strong>
                                    <span>{t('Partitions')}</span>
                                    <span>{t('CTE and pruning')}</span>
                                </div>
                                <span className="case-study-arrow"/>
                                <div className="case-study-stage case-study-stage-v4">
                                    <strong>V4 / Go</strong>
                                    <span>{t('Percentiles')}</span>
                                    <span>{t('Redis and prewarm')}</span>
                                </div>
                            </div>
                        </section>
                        <section className="case-study-performance">
                            <div>
                                <h4>{t('SQL plan optimization')}</h4>
                                <p>{t('Comparable EXPLAIN ANALYZE runs reduced the execution plan from 12.85 seconds to 1.27 seconds. The result depends on filters and environment, not being a universal API latency metric.')}</p>
                            </div>
                            <div className="case-study-result">
                                <span>{t('SQL execution plan')}</span>
                                <strong>12.85s <i>→</i> 1.27s</strong>
                                <span>{t('≈ 90% faster')}</span>
                            </div>
                        </section>
                    </div>
                </div>
            </div>
        </article>
    );
};

export default CaseStudyBlock;
